import type { CheckInTime, HotelItinerary } from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { CheckInTimeEnum } from "@hopper-b2b/types/src/lib/hotels-module/interfaces";
import dayjs from "dayjs";

const getCheckInTime = (checkInTime?: CheckInTime): string => {
  if (!checkInTime) {
    return "15:00:00";
  }
  if (checkInTime.CheckInTime === CheckInTimeEnum.Anytime) {
    return "00:00";
  }
  return checkInTime?.checkInTimeFrom ?? "15:00:00";
};

interface FormattedDateTime {
  dateFormatted: string;
  timeFormatted: string;
}

/**
 * Formats a date/time string into separate date and time components
 * @param dateTimeString - ISO date string (e.g., "2025-12-22T15:00:00")
 * @returns Object containing formatted date and time strings
 */
export const formatDateTime = (dateTimeString: string): FormattedDateTime => {
  const dateFormatted = dayjs(dateTimeString).format("MMMM Do, YYYY");
  const timeFormatted = dayjs(dateTimeString).format("h:mm A");

  return {
    dateFormatted,
    timeFormatted,
  };
};

interface ReservationDateTime {
  checkInFormatted: string;
  checkInTime: string;
  checkOutFormatted: string;
  checkOutTime: string;
  checkInFullFormatted: string;
  checkOutFullFormatted: string;
}

export const useReservationDateTime = (
  hotelReservation: HotelItinerary
): ReservationDateTime => {
  const { t } = useI18nContext();
  const hotelData = hotelReservation.reservation.lodgingData;

  // Build datetime strings
  const checkInDateTime =
    hotelReservation.reservation.checkInDate +
    "T" +
    getCheckInTime(hotelData?.checkInInstructions?.checkInTime);
  const checkOutDateTime =
    hotelReservation.reservation.checkOutDate +
    "T" +
    (hotelData?.checkInInstructions?.checkOutTime ?? "11:00:00");

  // Format dates and times using the formatter function
  const { dateFormatted: checkInFormatted, timeFormatted: checkInTime } =
    formatDateTime(checkInDateTime);
  const { dateFormatted: checkOutFormatted, timeFormatted: checkOutTime } =
    formatDateTime(checkOutDateTime);

  // Full formatted strings with "at" translation
  const checkInFullFormatted = t("common.dateTimeFormat", {
    date: checkInFormatted,
    time: checkInTime,
  });
  const checkOutFullFormatted = t("common.dateTimeFormat", {
    date: checkOutFormatted,
    time: checkOutTime,
  });

  return {
    checkInFormatted,
    checkInTime,
    checkOutFormatted,
    checkOutTime,
    checkInFullFormatted,
    checkOutFullFormatted,
  };
};
